import { Controller } from "@hotwired/stimulus";
import {
  CategoryScale,
  Chart,
  Filler,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import Chartkick from "chartkick";

// 图表由 Chartkick（数据协议）+ Chart.js（画布）负责，服务端只给容器和一份 JSON。
//
// 只注册「折线 + 面积」要用的零件：`chart.js/auto` 会把柱、雷达、气泡、桑基一起打进包，
// 整站目前只有「每日营收」一处画图，不值得为它多背几十 KB。**要加别的图型得先在这里
// register 对应零件**，少了零件 Chart.js 不报错，只是那张图静静不出现 —— 面积图尤其隐蔽：
// 线照画，只有渐变填充没了，因为填充面是 `Filler` 插件画的。
Chart.register(
  LineController,
  LineElement,
  PointElement,
  Filler,
  CategoryScale,
  LinearScale,
  Tooltip,
);
Chartkick.use(Chart);

// 颜色一律从 CSS 变量读：application.css 的 `:root` 是**唯一**的色值来源，这里不写十六进制。
// 为什么线色是 `--chart-line` 而不是 `--success`、渐变底端为什么不能是 `transparent`，
// 都写在 application.css 那几个令牌的注释里。
//
// ⚠ `--chart-line` 是 oklch 色。画布直接拿变量值当 `fillStyle` 没问题，但**任何要「算一算」的色**
// （Chart.js 的 hover 色）都会走 `@kurkle/color`，它只认 hex / rgb / hsl —— 喂 oklch 进去
// 不报错，一律回落成纯黑。所以 dataset 里的 hover 色全部显式给成常态色。
const THEME_VARS = {
  line: "--chart-line", // 折线本体，也是面积渐变的色相来源
  areaFrom: "--chart-area-from", // 渐变顶端
  areaTo: "--chart-area-to", // 渐变底端
  ink: "--foreground", // tooltip 衬底：与站内 `.tooltip` 同一个墨色
  paper: "--surface", // 墨底上的反白字（金额）
  inkMuted: "--chart-tooltip-title", // 墨底上的次级反白（日期）
  grid: "--separator",
  tick: "--muted",
};

// 金额文案的**唯一来源**：纵轴刻度与 tooltip 正文都走它，两处因此不可能各写一套。
// 与后端 `money()` 同口径（整数除法、无小数、带千分位）。服务端送来的已经是「元」的整数
// （`money_amount`），所以这里只负责加符号与分隔符 —— **别顺手加取整**：`Math.round`、
// `toFixed`、`Intl` 的 `maximumFractionDigits` 都是另一套口径，两处从此分叉。
export function yuan(value) {
  return `¥${Number(value).toLocaleString("zh-CN")}`;
}

// 日期文案：横轴要**缩写**（`9/1`，30 格排下来只有那么宽），tooltip 里给全称
// （`9 月 1 日`，与页面别处的日期同一个写法，见 config/locales 的 `:long`）。
// 同一个 key 要按两种密度呈现 ⇒ 转在 JS 里做，服务端仍然只送一份（`series` 的 key
// 同时喂横轴和 tooltip）。认不出的形状原样返回 —— 以后换成 ISO 日期也不会变成「undefined 月」。
export function dayLabel(label) {
  const parts = /^(\d{1,2})\/(\d{1,2})$/.exec(String(label));

  return parts
    ? `${Number(parts[1])} 月 ${Number(parts[2])} 日`
    : String(label);
}

// 线下的渐变填充。Chart.js 的 `backgroundColor` 允许给函数（脚本化取值），函数里拿得到
// 画布与绘图区，于是能把渐变建在「绘图区顶 → 基线」的整段高度上。
//
// ⚠⚠ **渐变必须是「整个数据集一条」，不能按每个点的高度各建一条**（试过，会静默失效）：
//   Chart.js 的折线填充是**一次路径、一次 `fill()`**。`backgroundColor` 虽然按点逐个求值
//   （30 天问 30 次），真正落到画布上的却只有其中一个结果 —— 抽中哪个是实现细节。按点建
//   渐变时，一旦抽到「这天营收为 0」那一格（它的面积就落在基线上 ⇒ 渐变零长度 ⇒ 全透明），
//   整块面积消失，而**不报错**：图看起来只是「线下面空着」，hover 重绘一次顺序变了、面积
//   又冒出来 —— 极难定位。反过来，按绘图区建的那一条对每个点求值结果都相同，用哪个都对。
//
// ⚠ 两个都不报错、只会画错的地方：
//   ① **首帧还没有 chartArea**（Chart.js 在量出绘图区之前就会问一次色；实测首帧那 30 次
//      调用 `chartArea` 全是 undefined），此时它的 `top` / `bottom` 是 undefined，拿去做
//      渐变会得到一个无效对象，图上什么都不填。这里先用纯色兜底，下一帧就换成渐变。
//   ② 脚本化取值是**逐点求值**的：一张 30 天的图会问 30 次，而 hover 每移一格又整张重绘
//      ⇒ 不缓存就是每帧造 30 个 CanvasGradient 对象。渐变只跟绘图区高度有关，所以按
//      「高度」记住上一次的结果。
export function areaFill(theme) {
  let gradient = null;
  let measured = "";

  return (context) => {
    const { ctx, chartArea } = context.chart;
    if (!chartArea) return theme.areaFrom;

    const key = `${chartArea.top}:${chartArea.bottom}`;
    if (key !== measured) {
      gradient = ctx.createLinearGradient(
        0,
        chartArea.top,
        0,
        chartArea.bottom,
      );
      gradient.addColorStop(0, theme.areaFrom);
      gradient.addColorStop(1, theme.areaTo);
      measured = key;
    }

    return gradient;
  };
}

// —— tooltip 的盒子尺寸 ——
//
// 这几个数字**同时喂两处**：画盒子的 `plugins.tooltip`，和位置函数里「这个盒子占多高」的
// 判断（见 `TOOLTIP_RESERVE`）。抄成两份就会在改字号时悄悄失配，而失配的方向很坏：
// 位置函数以为盒子很矮 ⇒ 贴着上沿的那一格（多半是最高点）被判成「上方放得下」⇒ Chart.js
// 把框钳回画布内 ⇒ 框正好盖住圆点。正是这一轮要修的那个毛病，只是换了个触发条件。
const TOOLTIP_BOX = {
  paddingY: 6, // 上下内边距
  titleSize: 11, // 日期
  bodySize: 15, // 金额
  titleGap: 3, // 两行之间（Chart.js 的 `titleMarginBottom`）
  caretSize: 4,
  // ⚠ 几何不变量：尖角顶端离点心正好 `caretPadding`（框边离点心 = caretSize + caretPadding，
  // 尖角再往回量 caretSize）。点外半径 = pointHoverRadius(5) + 1px 描边 = 6 ⇒ 必须 > 6，
  // 否则尖角刺进圆点。12 留 6px 余量；单测把这条与 pointHoverRadius 钉在一起。
  caretPadding: 12,
};

// 盒高 = 上下内边距 + 日期行 + 行距 + 金额行。Chart.js 的行高默认是**字号 × 1.2**
// （`toLineHeight`），标题一行、正文一项时不再叠加 spacing（见它的 `getTooltipSize`）。
// 这里**向上取整**：宁可多留一像素，也不能少。
export const TOOLTIP_BOX_HEIGHT = Math.ceil(
  TOOLTIP_BOX.paddingY * 2 +
    TOOLTIP_BOX.titleSize * 1.2 +
    TOOLTIP_BOX.titleGap +
    TOOLTIP_BOX.bodySize * 1.2,
);

// 一个方向要放得下这个盒子，需要多少净空：盒子 + 尖角 + 间隙。
export const TOOLTIP_RESERVE =
  TOOLTIP_BOX_HEIGHT + TOOLTIP_BOX.caretSize + TOOLTIP_BOX.caretPadding;

// —— tooltip 落在哪 ——
//
// Chart.js 自带的两个位置模式（`average` / `nearest`）只保证一件事：**框不超出画布**。
// 会不会压住数据点，它不管 —— 而这一页要看的就是那些点。坏在两处：
//
//   ① 压在点上的是**尖角**（caret，跟框同色），不是框本身 —— 见 TOOLTIP_BOX.caretPadding。
//   ② 点落在画布竖直中段时，`determineYAlign()` 会选 `center` —— 框与点齐平，同时
//      `determineXAlign()` 把框整个撂到点的左边或右边 ⇒ 视觉上就是「圆点被咬掉一块」，
//      且越靠左/越靠右越明显（`xAlign` 退成 left/right 时，框会贴着点去对齐画布边缘）。
//
// 所以落点自己定，规则一句话：**框放在点的上方、横向居中**；尖角从上方指向圆点，两边
// 谁都不碰谁（间隙见 `caretPadding` 那条）。上方放不下时才翻到下方（见下面函数里那段）。
export const TOOLTIP_POSITION = "clear-of-point";

// ⚠ 这是**位置函数**，Chart.js 以 `positioners[mode].call(tooltipModel, items, event)` 调它，
// 所以 `this` 是那个 tooltip 模型（`this.chart` 拿得到画布）。
//
// ⚠⚠ **不许读 `this.width` / `this.height`**：形状上很诱人（那就是框的实际高度），但
// Chart.js 的 `update()` 是**先跑位置函数、后量框**（`getTooltipSize()` 在它之后），
// 此刻这两个值还是**上一帧**的尺寸、首次悬停时甚至是 undefined ⇒ 落点会莫名其妙地漂。
// 盒高改从 `TOOLTIP_RESERVE`（我方配置算出来的静态上界）拿。
export function clearOfPointPositioner(items) {
  const point = items?.[0]?.element;
  // active 为空时 Chart.js 根本不会调到这里；真出现了就交回默认逻辑。
  if (!point) return false;

  const anchor = point.tooltipPosition();
  const roomAbove = anchor.y;
  const roomBelow = this.chart.height - anchor.y;
  const fitsAbove = roomAbove >= TOOLTIP_RESERVE;
  const fitsBelow = roomBelow >= TOOLTIP_RESERVE;

  // 默认放**上方**：面积图里线上方是空的（只有网格线），线下方是渐变填充 —— 把框放在
  // 没数据的那一侧。上方放不下才翻到下方，那是「最高点」那一格的常态。
  //
  // ⚠ 「放得下」必须**显式判**，不能只看哪边宽敞：规则既然是「默认上方」，上方不够时就必须
  //   翻面 —— 这两条规则在「点 y < 画布一半」那一段给出不同答案（y=99/画布 200 就是一格）。
  //   更硬的一条理由在 Chart.js 那边：`getBackgroundPoint()` 只把框钳制在
  //   `[0, 画布高 - 盒高]` 里，放不下的那一侧会让框**被推回来压住圆点**，而且不报错。
  //   所以预留量还必须小于画布高的一半（单测钉着）——否则两侧都可能放不下，「翻面」也救不了。
  //   两侧都放不下时（盒子比画布一半还高，h-48 的图上不会发生）退回「宽敞的一侧」尽力而为。
  const above = fitsAbove || (!fitsBelow && roomAbove >= roomBelow);

  return {
    // 这个 x/y 同时是尖角的指向目标（`update()` 里 `caretX/caretY = position.x/y`），
    // 所以它必须是**点心本身**，不是框的位置。
    x: anchor.x,
    y: anchor.y,
    xAlign: "center",
    // ⚠ `yAlign` 是**从框的角度**命名的：`bottom` 指「框贴着自己的下边缘」，即框落在点的
    // **上方**。别看反 —— 看反的后果是点在画布上半区时框压回自己身上。
    yAlign: above ? "bottom" : "top",
  };
}

// 位置模式表是**共享**的：插件对象上的 `positioners` 就是 Tooltip 模型查表用的那一张
// （`chart.js` 只导出插件，模型类不对外）。往这儿挂一个键，`plugins.tooltip.position`
// 写的模式名就认了。守卫见单测里的「模式必须真的被选上」。
Tooltip.positioners[TOOLTIP_POSITION] = clearOfPointPositioner;

// 图表配置。**纯函数**：只吃数据与主题，不碰 DOM、不碰 Chartkick ——
// test/javascript/chart_controller_test.js 直接压它，不需要浏览器。
export function chartOptions(theme) {
  const fill = areaFill(theme);

  return {
    colors: [theme.line],
    dataset: {
      // 面积图 = 一条折线 + 线下的渐变填充。`fill: true`（填到 y=0 基线）上游按图型也会给，
      // 这里显式再写一次：图型一旦被改成 line，整张图会**没有面积**却不报错 —— 而这一页
      // 要看的就是面积。
      fill: true,
      // 线宽 2 是站内统一的笔画档（图标也是 2），比 Chart.js 折线默认的 3 细一档。
      borderWidth: 2,
      borderColor: theme.line,
      // 线要实、面要淡：填充走垂直渐变，视觉重量落在线上而不是面上。
      backgroundColor: fill,

      // ⚠ **必须关掉按数据集裁剪**（Chart.js 默认把数据集裁在绘图区内，只给 `defaultClip()`
      //   算出的那点余量 —— 折线的余量是 `max(borderWidth, 首尾点尺寸)/2` = 1px）。
      //   于是**落在边界上的点只剩一半**：营收为 0 的那天（点正好在基线上）、首尾两天（贴着左右沿）
      //   各被切掉一块 —— 而那几天恰恰是最常 hover 的。最高那天要看 y 轴上沿：本月数据的上限是
      //   ¥4,000、最高只到 ¥3,218，还留着余量，所以那一点暂时切不到，换个区间就会。
      //   症状是「圆点被咬掉一块」，且**不报错**：看起来像 tooltip 压住了它，实际是 canvas 裁的
      //   （本页第一版就是这么误诊的 —— 去改 tooltip 落点，怎么改都不好）。
      //   关掉是唯一一次救四边的做法：`clip: {bottom: 6}` 这种只补一边，点跑到上沿/边角照样缺。
      clip: false,

      // ⚠ hover 的每一个色**都必须显式给**，且都给常态色：不给的话 Chart.js 会拿常态色跑一遍
      // `getHoverColor()`（`@kurkle/color`），oklch 会被它算成纯黑（见文件头）。
      // 不做「hover 深一档」是因为 hover 的反馈已经由 tooltip 承担，再变一次色反而吵。
      hoverBorderColor: theme.line,
      hoverBackgroundColor: fill,

      // 数据点：多天时收进线里（30 个点各自露头会连成一条毛毛虫），靠 hover 露出来。
      // 但**只有一格数据时**（区间 = 「今天」）折线退化成孤点 —— 那一刻点半径若是 0，
      // 整张图就是空白，看起来跟图表坏掉一样。所以半径跟着点数走：脚本化取值逐点求值，
      // 这里对每个点返回同一个数。
      pointRadius: (context) => (context.dataset.data.length === 1 ? 5 : 0),
      pointHoverRadius: 5,
      pointBorderColor: theme.line,
      pointBackgroundColor: theme.line,
      pointHoverBorderColor: theme.line,
      pointHoverBackgroundColor: theme.line,

      // 直线段，不做贝塞尔平滑。每日营收是离散读数、且多数天是 0，平滑曲线会在 0 附近
      // 过冲，把「某一天有营收」糊成「连着两三天都有」—— 那是改变读数，不是样式。
      tension: 0,
    },
    library: {
      // 图表画在 canvas 上，不吃 CSS 的继承 —— 字体得显式给，否则全站唯一一处写着别的字体。
      font: { family: theme.fontFamily },
      plugins: {
        legend: { display: false },
        // 站内 `.tooltip`（application.css）= 墨色底 / 反白 / 4px 圆角。画布上照同一份语言写，
        // 否则图上会冒出一个跟全站无关的框（Chart.js 的默认是一层不透明度 0.8 的黑底 +
        // 加粗白标题 + 6px 圆角，小屏上几乎盖掉半张图）。
        //
        // **两行，且两行不等重**：日期是「这是哪一天」的定位信息，小一号、颜色压到反白纸色的
        // 六成半、放上面一行；金额是这一眼真正要看的东西，大一号、纯反白、放下面一行。
        // 早先把两段并成一行（`9/1 · ¥1,638`）时它们等重，一眼分不出哪个是数 —— 而这一页
        // 的图本来就是拿来读数的。层次全靠这里的字体与颜色两组值，缺一组就退化成两行同款。
        tooltip: {
          backgroundColor: theme.ink,
          titleColor: theme.inkMuted,
          titleFont: {
            family: theme.fontFamily,
            size: TOOLTIP_BOX.titleSize,
            weight: "500",
          },
          titleAlign: "left",
          titleMarginBottom: TOOLTIP_BOX.titleGap,
          bodyColor: theme.paper,
          bodyFont: {
            family: theme.fontFamily,
            size: TOOLTIP_BOX.bodySize,
            weight: "600",
          },
          bodyAlign: "left",
          padding: {
            top: TOOLTIP_BOX.paddingY,
            bottom: TOOLTIP_BOX.paddingY,
            left: 10,
            right: 10,
          },
          cornerRadius: 4,
          // 落点自己算（见 clearOfPointPositioner）：`average`/`nearest` 会让尖角扎进圆点。
          position: TOOLTIP_POSITION,
          caretSize: TOOLTIP_BOX.caretSize,
          caretPadding: TOOLTIP_BOX.caretPadding, // 几何不变量，见 TOOLTIP_BOX
          // 只有一条数据，色块（displayColors）是纯噪音。Chartkick 也关了，这里明写是因为
          // Chart.js 的默认值是 true —— 读这份配置的人不该靠「上游替我关了」来判断。
          displayColors: false,
          callbacks: {
            // 一行一条信息、各占一个槽位：日期进标题槽、金额进正文槽。Chart.js 的默认 title
            // 会把类目名原样返回（那就成了两行 `9/1` + `¥1,638`），而 Chartkick 只在「没人给
            // label」时才补自己的那个 ⇒ 这两个回调必须成对给。
            title: (items) => [dayLabel(items[0]?.label)],
            label: (item) => yuan(item.parsed.y),
          },
        },
      },
      scales: {
        // 不画竖向网格：折线的每个点自己标了横向位置，30 条竖线只会把图糊掉。
        // 横轴标签会随容器宽度自动抽稀（Chart.js 的 `autoSkip`），手机上不会糊成一团 ——
        // 这正是换掉旧 SVG 的理由：它是等比缩放的，375 视口下轴标签只剩 5.2px。
        x: {
          grid: { display: false },
          border: { color: theme.grid },
          ticks: { color: theme.tick, maxRotation: 0, autoSkipPadding: 12 },
        },
        y: {
          beginAtZero: true,
          border: { display: false },
          grid: { color: theme.grid },
          // 与 tooltip 正文同一个 `yuan()` —— 图上两个位置的金额不可能长得不一样。
          ticks: { color: theme.tick, callback: (value) => yuan(value) },
        },
      },
    },
  };
}

export default class extends Controller {
  static values = {
    data: { type: Object, default: {} },
    type: { type: String, default: "area" },
  };

  // `area` ⇒ `Chartkick.AreaChart`。做成 getter 是为了让前端单测能把假的图表类换进来
  // （test/javascript/ 用 `Object.create(prototype)` 造实例，那里没有 canvas、也不跑 Chart.js）。
  get chartClass() {
    const name = `${this.typeValue[0].toUpperCase()}${this.typeValue.slice(1)}Chart`;
    return Chartkick[name];
  }

  // 主题 = CSS 变量 + 跟随容器的字体。抽成方法同样是为了单测能替换掉。
  theme() {
    const root = getComputedStyle(document.documentElement);
    const colors = Object.fromEntries(
      Object.entries(THEME_VARS).map(([key, name]) => [
        key,
        root.getPropertyValue(name).trim(),
      ]),
    );

    return { ...colors, fontFamily: getComputedStyle(this.element).fontFamily };
  }

  connect() {
    this.chart = new this.chartClass(
      this.element,
      this.dataValue,
      chartOptions(this.theme()),
    );
  }

  // turbo frame 换掉这块 DOM 时 Stimulus 会走这里，**必须显式销毁**：Chart.js 把实例挂在
  // canvas 上并监听 window 的 resize，只把 DOM 扔掉不会回收 —— 每切一次时间区间就多留一份，
  // 页面不报错，只是越用越沉。Chartkick 自己的清理只认整页的 `turbo:before-render`，够不着这里。
  disconnect() {
    this.chart?.destroy();
    this.chart = null;
  }
}
